import { useEffect, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import { MAPS_KEY } from "@/lib/api";
import { Store, Home, Bike, Clock, Wifi, WifiOff } from "lucide-react";

function Pin({ color, icon: Icon, pulse }) {
  return (
    <div className="flex flex-col items-center" style={{ transform: "translateY(-50%)" }}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${pulse ? "animate-pulse" : ""}`} style={{ background: color }}>
        <Icon className="w-4 h-4 text-white" />
      </div>
    </div>
  );
}

// Draws the pickup → courier → destination route inside the Google Map.
function Route({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google || !points || points.length < 2) return;
    const line = new window.google.maps.Polyline({
      path: points, geodesic: true, strokeColor: "#F97316", strokeOpacity: 0.9, strokeWeight: 4,
    });
    line.setMap(map);
    const b = new window.google.maps.LatLngBounds();
    points.forEach((p) => b.extend(p));
    map.fitBounds(b, 70);
    return () => line.setMap(null);
  }, [map, JSON.stringify(points)]);
  return null;
}

function relTime(t, isoStr) {
  if (!isoStr) return null;
  const secs = Math.max(0, (Date.now() - new Date(isoStr).getTime()) / 1000);
  if (secs < 45) return t("track.justNow");
  return t("track.minsAgo", { m: Math.round(secs / 60) });
}

export default function TrackingMap({ pickup, destination, courier, phase, active = true, etaMinutes, online, stale, lastUpdated, height = 340 }) {
  const { t } = useTranslation();
  const [mapError, setMapError] = useState(false);
  // Google fires gm_authFailure on invalid key / referrer / billing issues — fall back gracefully.
  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);
  const dest = destination && destination.lat != null ? { lat: destination.lat, lng: destination.lng } : null;
  const src = pickup && pickup.lat != null ? { lat: pickup.lat, lng: pickup.lng } : null;
  const cour = courier && courier.lat != null ? { lat: courier.lat, lng: courier.lng } : null;
  const center = cour || src || dest || { lat: 41.0082, lng: 28.9784 };
  const routePts = [src, cour, dest].filter(Boolean);
  const etaText = etaMinutes != null ? (etaMinutes <= 1 ? t("track.arriving") : t("track.minLeft", { m: etaMinutes })) : null;

  const StatusOverlay = () => (
    <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 z-10 pointer-events-none" dir="auto">
      <div data-testid="track-phase" className="bg-slate-900/85 backdrop-blur text-white rounded-xl px-3 py-2 text-xs space-y-0.5 shadow-lg">
        <div className="font-semibold text-brand">{phase === "to_customer" ? t("track.toCustomer") : t("track.toStore")}</div>
        {active && etaText && <div data-testid="track-eta" className="flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {t("track.eta")}: {etaText}</div>}
      </div>
      {active && (
        <div data-testid="track-signal" className={`rounded-full px-2.5 py-1 text-xs font-medium shadow flex items-center gap-1 ${stale ? "bg-amber-500 text-white" : online ? "bg-emerald-500 text-white" : "bg-slate-500 text-white"}`}>
          {stale || !online ? <WifiOff className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
          {stale ? t("track.stale") : online ? t("track.online") : t("track.offline")}
        </div>
      )}
    </div>
  );

  const FooterOverlay = () =>
    active && (cour || lastUpdated) ? (
      <div data-testid="track-updated" className="absolute bottom-3 left-3 z-10 bg-slate-900/85 backdrop-blur text-slate-200 rounded-xl px-3 py-1.5 text-xs flex items-center gap-2 shadow-lg" dir="auto">
        {cour ? <span>{t("track.updated")}: {relTime(t, lastUpdated)}</span> : <span>{t("track.waiting")}</span>}
      </div>
    ) : null;

  if (!MAPS_KEY || mapError) {
    return (
      <div data-testid="map-fallback" className="rounded-2xl border bg-slate-900 text-slate-200 relative overflow-hidden hero-grid" style={{ height }} dir="auto">
        <div className="absolute inset-0 opacity-40 hero-grid" />
        <StatusOverlay />
        <div className="relative z-0 h-full flex flex-col justify-center gap-2 p-5 text-sm">
          {src && <div className="flex items-center gap-2"><Store className="w-4 h-4 text-sky-400" /> {t("track.store")} · {src.lat.toFixed(4)}, {src.lng.toFixed(4)}</div>}
          {cour && active && <div className="flex items-center gap-2 animate-pulse"><Bike className="w-4 h-4 text-brand" /> {t("track.courier")} · {cour.lat.toFixed(4)}, {cour.lng.toFixed(4)}</div>}
          {dest && <div className="flex items-center gap-2"><Home className="w-4 h-4 text-emerald-400" /> {t("track.you")} · {dest.lat.toFixed(4)}, {dest.lng.toFixed(4)}</div>}
          <div className="text-xs text-slate-400 pt-2 max-w-md">{t("track.noKey")}</div>
        </div>
        <FooterOverlay />
      </div>
    );
  }

  return (
    <div data-testid="map-live" className="rounded-2xl overflow-hidden border relative" style={{ height }}>
      <StatusOverlay />
      <APIProvider apiKey={MAPS_KEY} onError={() => setMapError(true)}>
        <Map style={{ width: "100%", height: "100%" }} defaultCenter={center} defaultZoom={13} mapId="drivons-market-map" gestureHandling="greedy" disableDefaultUI={true} zoomControl={true}>
          {src && <AdvancedMarker position={src}><Pin color="#0284C7" icon={Store} /></AdvancedMarker>}
          {dest && <AdvancedMarker position={dest}><Pin color="#10B981" icon={Home} /></AdvancedMarker>}
          {cour && active && <AdvancedMarker position={cour}><Pin color="#F97316" icon={Bike} pulse /></AdvancedMarker>}
          <Route points={routePts} />
        </Map>
      </APIProvider>
      <FooterOverlay />
    </div>
  );
}
